import { createContext, useCallback, useContext, useEffect, useState } from 'react'

export interface PageHeaderBreadcrumb {
  href: string
  children: string
}

type NewButtonType = 'invoice' | 'order' | 'expense' | 'participant'

interface PageHeaderState {
  breadcrumbs: PageHeaderBreadcrumb[]
  newButton?: NewButtonType
}

interface PageHeaderContextValue extends PageHeaderState {
  setPageHeader: (state: PageHeaderState) => void
  resetPageHeader: () => void
}

const defaultState: PageHeaderState = {
  breadcrumbs: [],
  newButton: undefined,
}

const PageHeaderContext = createContext<PageHeaderContextValue | null>(null)

export function PageHeaderProvider({ children }: { children: React.ReactNode }) {
  const [state, setState] = useState<PageHeaderState>(defaultState)

  const setPageHeader = useCallback((next: PageHeaderState) => {
    setState(next)
  }, [])

  const resetPageHeader = useCallback(() => {
    setState(defaultState)
  }, [])

  return (
    <PageHeaderContext.Provider value={{ ...state, setPageHeader, resetPageHeader }}>
      {children}
    </PageHeaderContext.Provider>
  )
}

export function usePageHeader() {
  const context = useContext(PageHeaderContext)
  if (!context) {
    throw new Error('usePageHeader must be used within a PageHeaderProvider')
  }
  return context
}

export function useSetPageHeader(opts: {
  breadcrumbs?: PageHeaderBreadcrumb[]
  newButton?: NewButtonType
}) {
  const { setPageHeader, resetPageHeader } = usePageHeader()

  useEffect(() => {
    setPageHeader({
      breadcrumbs: opts.breadcrumbs ?? [],
      newButton: opts.newButton,
    })
    return () => resetPageHeader()
  }, [JSON.stringify(opts.breadcrumbs), opts.newButton])
}
